/* eslint-disable import/no-unresolved */
import { MenuShell, WorkspaceApp, WorkspaceProvider } from '@desygner/ui-editor-menus';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { constants } from '@config';
import Store, { actions } from '@store';
import ReactAppBuilder from 'src/utils/react-app-builder';

import AssetManager from './AssetManager';
import {
  getLayoutBoxThumbnail,
  getLayoutBoxCoord,
  getHoveredElement,
  getFullSize,
  getElementType
} from './assets.utils';

const BUTTONS = {
  ADD: {
    title: 'Add Image',
    name: 'add-image',
    icon: 'plus---circle'
  },
  REPLACE: {
    title: 'Replace Image',
    name: 'replace',
    icon: 'arrows-circle'
  },
  BACKGROUND: {
    title: 'Use as Background',
    name: 'use-background',
    icon: 'background---20'
  }
};

const TEXT_BUTTONS = {
  ADD: {
    title: 'Add Text',
    name: 'add-image',
    icon: 'editor-plus'
  }
};

const instances = {};
const collection = {
  workspace: 'assets',
  user: 'library'
};

export class WorkspaceView extends ReactAppBuilder {
  constructor ({ appId, targetNodeSelector, type, collection: resourceCollection }) {
    if (instances[`${appId}-${targetNodeSelector}-${type}`]) {
      return instances[`${appId}-${targetNodeSelector}-${type}`];
    }

    super({ appId, targetNodeSelector });

    this.type = type;
    this.collection = resourceCollection === collection.user ? collection.user : collection.workspace;
    this.canvas = INKIVE.EditorFacade.canvas;

    instances[`${appId}-${targetNodeSelector}-${type}`] = this;
  }

  closeApp () {
    super.closeApp(this.App);
  }

  mountApp () {
    super.mountApp(this.App);
  }

  #createImage = (url, size, smartAsset) => {
    if (!INKIVE.RestrictionsFacade.isFunctionalityAllowed('image_add')) {
      return;
    }

    const id = INKIVE.DesignFacade.create('image', {
      imported: false,
      source: {
        url,
        external_resize: false
      },
      smart_asset: smartAsset,
      position: {
        x: size.x,
        y: size.y
      },
      size: {
        width: size.width,
        height: size.height
      },
      ab_id: null
    });

    if (this.collection === collection.workspace) {
      const newElement = document.getElementById(id);
      newElement.setAttribute('from-library', 'true'); // Why are we setting attributes here instead of facade?
      this.canvas.clearSelection();
      this.canvas.selectOnly([newElement]);
    }
  };

  #replaceImage (url, size, smartAsset, asset) {
    const selectedElement = INKIVE.DesignFacade.getSelectedElements()[0];

    if (!selectedElement) {
      return;
    }

    const $element = $(selectedElement);
    const isFrame = $element.is('.sticker') && $element.find('image.subelement').length;
    const isImage = $element.is('image.element');

    if (!isFrame && !isImage) {
      return;
    }

    const selectedElemUrl = selectedElement.getAttribute('xlink:href');
    const isPlaceHolder = selectedElemUrl && /imageplaceholder_new\.svg$/.test(selectedElemUrl);
    const id = $element.closest('.background').length
      ? 'background'
      : $element.attr('id');

    INKIVE.ImagesFactory.removeImageFilter($element, url);

    INKIVE.DesignFacade.set(id, {
      source: {
        preload: true,
        url
      },
      keep_box_size: true,
      img_frame: isPlaceHolder,
      just_added: asset.details,
      smart_asset: smartAsset
    });
  }

  #useAsBackground (url, smartAsset, asset) {
    this.canvas.clearSelection();

    INKIVE.DesignFacade.set('background', {
      source: url,
      just_added: asset.details,
      smart_asset: smartAsset
    });
  }

  #createSticker (url, size, smartAsset, s3Source) {
    if (!INKIVE.RestrictionsFacade.isFunctionalityAllowed('logo_add') && this.type === 'logo') return;
    if (!INKIVE.RestrictionsFacade.isFunctionalityAllowed('icon_add') && this.type === 'icon') return;

    const isVector = url.endsWith('.svg');
    const id = INKIVE.DesignFacade.create(this.type, {
      is_vector: isVector, // TODO: change this to data.type
      s3_source: s3Source,
      resize: !isVector,
      thumb_src: url,
      smart_asset: smartAsset,
      position: {
        x: size.x,
        y: size.y
      },
      size
    });

    if (this.collection === collection.workspace) {
      const newElement = document.getElementById(id);
      newElement.setAttribute('from-library', 'true'); // Why are we setting attributes here instead of facade?
      this.canvas.clearSelection();
      this.canvas.selectOnly([newElement]);
    }
  }

  #replaceSticker (url, size, smartAsset, asset, s3Source) {
    const selectedElement = INKIVE.DesignFacade.getSelectedElements()[0];

    if (!selectedElement) {
      return;
    }
    const assetSize = asset.details.size ?? size;
    const imageRatio = assetSize.width / assetSize.height || 1;
    const id = selectedElement.getAttribute('id');

    if (selectedElement.classList.contains('sticker')) {
      selectedElement.classList.remove('sticker');
      selectedElement.classList.add('icon');
    }

    const selectedElementWidth = Number(selectedElement.dataset.boundingWidth) || selectedElement.getBBox().width;
    const selectedElementHeight = Number(selectedElement.dataset.boundingHeight) || selectedElement.getBBox().height;

    selectedElement.removeAttribute(constants.smartAssets.attributes.definedAsset);
    selectedElement.removeAttribute(constants.smartAssets.attributes.userInput);

    const newHeight = selectedElementWidth / imageRatio;
    const isTheSameSize = Math.abs(newHeight - selectedElementHeight) <= 2;

    INKIVE.DesignFacade.set(id, {
      is_vector: url.endsWith('.svg'), // TODO: change this to data.type
      thumb_src: url,
      s3_source: s3Source,
      smart_load: true,
      just_added: asset.details,
      smart_asset: smartAsset,
      size: {
        width: selectedElementWidth,
        height: isTheSameSize ? selectedElementHeight : newHeight
      }
    });

    if (this.collection === collection.workspace) {
      const newElement = document.getElementById(id);
      newElement.setAttribute('from-library', 'true'); // Why are we setting attributes here instead of facade?
      this.canvas.clearSelection();
      this.canvas.selectOnly([newElement]);
    }
  }

  #insertText (content, smartAsset) {
    if (INKIVE.EditorFacade.canvas.getMode() !== 'textedit') return;

    const selectedElements = INKIVE.DesignFacade.getSelectedElements('text');
    const canChangeContent = INKIVE.RestrictionsFacade.canChange(selectedElements[0], 'change_content');

    if (selectedElements.length === 1 && canChangeContent) {
      INKIVE.EditorFacade.canvas.runExtensions('insertContent', {
        content,
        elem: selectedElements[0],
        smart_asset: smartAsset,
        shouldRemoveOverlappedStyles: true
      });
    }
  }

  #updateText (content, smartAsset) {
    if (INKIVE.EditorFacade.canvas.getMode() === 'textedit') {
      this.#insertText(content, smartAsset);
      return;
    }

    const selectedElements = INKIVE.DesignFacade.getSelectedElements('text');
    const selectedElement = selectedElements[0];
    const canChangeContent = INKIVE.RestrictionsFacade.canChange(selectedElement, 'change_content');

    if (!selectedElement || !canChangeContent) {
      return;
    }

    const id = selectedElement.getAttribute('id');

    INKIVE.DesignFacade.set(id, {
      content,
      smart_asset: this.collection === collection.workspace && smartAsset,
      remove_partial_styles: true
    });

    if (this.collection === collection.workspace) {
      const newElement = document.getElementById(id);
      newElement.setAttribute('from-library', 'true'); // Why are we setting attributes here instead of facade?
      this.canvas.clearSelection();
      this.canvas.selectOnly([newElement]);
    }
  }

  #createText (content, smartAsset, dropPosition) {
    const state = Store.getState();
    const { canvasRelativeSize, zoom } = state.editor;

    const font = this.#getFont(canvasRelativeSize);
    const size = {
      width: this.#getWidth(content, font.size, canvasRelativeSize, zoom)
    };

    const position = {
      x: dropPosition.x,
      y: dropPosition.y
    };

    INKIVE.DesignFacade.create('text', {
      library_text: true,
      smart_asset: smartAsset,
      content,
      size,
      position,
      font
    });
  }

  #createVideo (thumb, size, smartAsset, asset) {
    const isYoutube = !asset.details.data.file && !!asset.details.data.source;
    const source = asset.details.data.file || asset.details.data.source;

    INKIVE.DesignFacade.create(this.type, {
      position: {
        x: size.x,
        y: size.y
      },
      size,
      thumb_src: thumb,
      source: isYoutube ? source : undefined,
      url: isYoutube ? undefined : source,
      smart_asset: smartAsset,
      from_library: this.collection === collection.workspace
    });
  }

  #replaceVideo (thumb, smartAsset, asset) {
    const [target, ...multipleElementsSelected] = this.canvas.getSelectedElems();
    if (!target || multipleElementsSelected.length) return;

    INKIVE.DesignFacade.set(target.id, {
      source: asset.details.data.file || asset.details.data.source,
      thumb_src: thumb,
      smart_asset: smartAsset,
      just_added: asset.details,
      keep_box_size: true
    });

    this.canvas.clearSelection();
    this.canvas.selectOnly([target]);
  }

  #getWidth = (text, fontSize, canvas, zoom) => {
    const halfCanvas = canvas.width / 2;
    const textLengthSize = text.length * 2 * fontSize * zoom;

    return (textLengthSize > halfCanvas) ? canvas.width - 40 : textLengthSize;
  };

  #getFont = (canvas) => {
    const baseFont = constants.tableOfContents.defaultHeadingStyle[0].size;
    const baseSize = Math.min(canvas.height, canvas.width);
    const fontSize = Math.round(baseSize * baseFont);

    return {
      size: fontSize,
      family: constants.tableOfContents.defaultHeadingStyle[0].fontFamily
    };
  };

  async #getImageSize (url, fallbackUrl) {
    const isValidUrl = url && url.startsWith('http');
    if (!isValidUrl) return;

    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        return resolve({ width: img.width || 0, height: img.height || 0 });
      };
      img.onerror = () => {
        if (fallbackUrl && fallbackUrl.startsWith('http')) {
          img.src = fallbackUrl;
          img.onerror = reject; // Reject if the fallback URL also fails
        } else {
          reject(new Error('Image failed to load and no valid fallback URL provided'));
        }
      };

      img.src = url;
    });
  }

  #calculateRelativeAssetSize (canvasSize, assetSize) {
    const SVGRatio = canvasSize.width / canvasSize.height;
    const imageRatio = assetSize.width / assetSize.height;
    const sizeLimit = this.type === 'logo' ? 0.4 : 0.7;

    const width = imageRatio > SVGRatio
      ? canvasSize.width * sizeLimit
      : (canvasSize.height * sizeLimit) * imageRatio;

    const height = imageRatio > SVGRatio
      ? width / imageRatio
      : canvasSize.height * sizeLimit;

    return {
      width,
      height
    };
  }

  async #getSize (asset, thumb, s3Source) {
    const canvasWidth = INKIVE.EditorFacade.canvas.contentW;
    const canvasHeight = INKIVE.EditorFacade.canvas.contentH;

    if (this.type === 'text' && !asset.details.rect) {
      return {
        x: 20,
        y: 20
      };
    }

    if (asset.details.rect) {
      const zoom = INKIVE.EditorFacade.handle('zoom', 'get');
      const offset = INKIVE.EditorFacade.editor.getOffset();

      return {
        width: Math.floor4(asset.details.rect.width / zoom),
        height: Math.floor4(asset.details.rect.height / zoom),
        x: Math.floor4((asset.details.rect.x - offset.x) / zoom),
        y: Math.floor4((asset.details.rect.y - offset.y) / zoom)
      };
    } else if (asset.details.size) {
      const {
        width, height
      } = this.#calculateRelativeAssetSize({
        width: canvasWidth, height: canvasHeight
      }, asset.details.size);
      const imageVersionUrl = INKIVE.ImagesFactory.getRightSizeUrl(
        thumb,
        { width, height }
      );
      const isSvg = imageVersionUrl?.endsWith('.svg');

      const fitSize = isSvg
        ? {
            width, height
          }
        : await this.#getImageSize(imageVersionUrl, s3Source);

      return {
        width: fitSize.width,
        height: fitSize.height,
        x: (canvasWidth - fitSize.width) / 2,
        y: (canvasHeight - fitSize.height) / 2
      };
    } else if (thumb) {
      const size = await this.#getImageSize(thumb);

      const {
        width, height
      } = this.#calculateRelativeAssetSize({
        width: canvasWidth, height: canvasHeight
      }, size);

      return {
        width,
        height,
        x: (canvasWidth - width) / 2,
        y: (canvasHeight - height) / 2
      };
    }
  }

  async #loadUserPermission () {
    const permissions = await INKIVE.UserInfoFactory.getPermissions();
    const isWorkspaceCollection = this.collection === collection.workspace;

    return {
      assetManagement: isWorkspaceCollection ? permissions.assets_manage : true
    };
  }

  App = () => {
    const selectedElements = useSelector(state => state.canvas.selected.elements);
    const activePageId = useSelector(state => state.editor.activePage);
    const animationFrameId = useRef(null);
    const layoutRef = useRef(null);
    const layoutAttributeRef = useRef(null);
    const [permissions, setPermissions] = useState({});
    const bucketName = INKIVE.C.BUCKET;

    useEffect(() => {
      const loadUserPermission = async () => {
        const response = await this.#loadUserPermission();
        setPermissions(response);
      };

      loadUserPermission();
    }, []);

    const isReplaceRestricted = useMemo(() => {
      if (selectedElements.length !== 1) return true;

      if (!INKIVE.RestrictionsFacade.isRestrictedDesign()) return false;

      const restriction = this.type === 'image' ? 'image_content' : `${this.type}_replace`;
      return INKIVE.RestrictionsFacade.getElementRestriction(selectedElements[0].id, restriction) === false;
    }, [selectedElements]);

    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
      const mouseMoveHandler = (event) => {
        if (animationFrameId.current) {
          cancelAnimationFrame(animationFrameId.current);
        }

        animationFrameId.current = requestAnimationFrame(() => {
          // TODO: Move this to a helper

          const offset = INKIVE.EditorFacade.editor.getOffset();
          const overlayElement = document.getElementById('react-drag-overlay-element');
          if (!overlayElement || (!overlayElement.getAttribute('src') && this.type !== 'text')) return;

          const options = {
            imageRatio: 1,
            mouse_x: event.clientX - offset.x,
            mouse_y: event.clientY - offset.y,
            skipHistory: true
          };

          const layout = getHoveredElement(options.mouse_x, options.mouse_y, this.type);

          if (layoutRef.current === layout) return;

          const restriction = this.type === 'image' ? 'image_content' : `${this.type}_replace`;
          if (INKIVE.RestrictionsFacade.getElementRestriction(layout?.id, restriction) === false) return;

          if (layoutRef.current) {
            if (layoutAttributeRef.current) {
              const thumbnail = getLayoutBoxThumbnail(layoutRef.current);
              thumbnail.setAttribute('xlink:href', layoutAttributeRef.current.url);
              thumbnail.setAttribute('x', layoutAttributeRef.current.x);
              thumbnail.setAttribute('y', layoutAttributeRef.current.y);
              thumbnail.setAttribute('width', layoutAttributeRef.current.width);
              thumbnail.setAttribute('height', layoutAttributeRef.current.height);
            }

            layoutRef.current = null;
            layoutAttributeRef.current = null;
          }

          if (layout && !layoutRef.current) {
            const elementType = getElementType(layout);
            if (elementType === 'image') {
              const subelement = layout.querySelector('.subelement');
              const layoutElement = subelement && !layout.getAttribute('xlink:href')
                ? subelement
                : layout;

              const thumbnail = getLayoutBoxThumbnail(layoutElement);
              const layoutPrvAttr = {
                x: thumbnail.getAttribute('x'),
                y: thumbnail.getAttribute('y'),
                url: thumbnail.getAttribute('xlink:href'),
                width: thumbnail.getAttribute('width'),
                height: thumbnail.getAttribute('height')
              };

              layoutAttributeRef.current = layoutPrvAttr;

              const clipPathBB = getLayoutBoxCoord(layout, 'image');
              const size = getFullSize({ width: overlayElement.width, height: overlayElement.height }, clipPathBB);

              thumbnail.setAttribute('xlink:href', overlayElement.getAttribute('src'));
              thumbnail.setAttribute('x', clipPathBB.x - size.x);
              thumbnail.setAttribute('y', clipPathBB.y - size.y);
              thumbnail.setAttribute('width', size.width * 1);
              thumbnail.setAttribute('height', size.height * 1);
            }

            this.canvas.selectOnly([layout]);

            layoutRef.current = layout;
          }
        });
      };

      if (isDragging) {
        document.addEventListener('mousemove', mouseMoveHandler);
      } else {
        if (animationFrameId.current) {
          cancelAnimationFrame(animationFrameId.current);
        }

        document.removeEventListener('mousemove', mouseMoveHandler);
      }

      return () => {
        if (animationFrameId.current) {
          cancelAnimationFrame(animationFrameId.current);
        }

        document.removeEventListener('mousemove', mouseMoveHandler);
      };
    }, [isDragging, isReplaceRestricted]);

    const cardButtons = useMemo(() => {
      const result = [];

      if (INKIVE.RestrictionsFacade.isFunctionalityAllowed(`${this.type}_add`)) {
        this.type === 'text'
          ? result.push(TEXT_BUTTONS.ADD)
          : result.push(BUTTONS.ADD);
      }

      if (selectedElements.length === 1 && !isReplaceRestricted) {
        const element = document.getElementById(selectedElements[0].id);
        if (element) {
          const isMatchType = INKIVE.DesignFacade.getType(element) === this.type;
          const isPlaceholder = element.classList.contains('placeholder-frame') && this.type === 'image';
          if (isMatchType || isPlaceholder) {
            result.push(BUTTONS.REPLACE);
          }
        }
      }

      if (this.type === 'image' && INKIVE.RestrictionsFacade.isReplaceAllowed('background')) {
        result.push(BUTTONS.BACKGROUND);
      }

      return result;
    }, [selectedElements, activePageId]);

    const userToken = useSelector(state => state.user.userInfo.credentials.api_key);

    const credentials = useMemo(() => ({
      baseUrl: INKIVE.C.API_URL,
      userToken
    }), [userToken]);

    const onAssetClick = useCallback(async (asset) => {
      if (asset.action === 'start-dragging') {
        setIsDragging(true);
      } else {
        setIsDragging(false);
      }

      const isAddAction = asset.action === 'add-image' || asset.action === undefined || (asset.action === 'drop' && !layoutRef.current);
      const isReplaceAction = asset.action === 'replace' || (asset.action === 'drop' && layoutRef.current);
      const isUseAsBackgroundAction = asset.action === 'use-background';
      const source = asset.source ?? INKIVE.ImagesFactory.getVersionUrl(asset.source, 'thumb');
      const s3Source = asset.details?.data?.source;
      const isStickerType = this.type === 'logo' || this.type === 'icon';
      const isWorkspaceCollection = this.collection === collection.workspace;
      const smartAsset = isWorkspaceCollection && asset.details.id;
      const size = await this.#getSize(asset, source, s3Source);

      if (!asset.source) return;
      if (asset.details) {
        Store.dispatch(actions.assets.setDefinedAssets([asset.details]));
      }

      if (this.type === 'image' && isAddAction) {
        this.#createImage(source, size, smartAsset);
      } else if (this.type === 'image' && isReplaceAction) {
        this.#replaceImage(source, size, smartAsset, asset);
      } else if (this.type === 'image' && isUseAsBackgroundAction) {
        this.#useAsBackground(source, smartAsset, asset);
      } else if (isStickerType && isAddAction) {
        this.#createSticker(source, size, smartAsset, s3Source);
      } else if (isStickerType && isReplaceAction) {
        this.#replaceSticker(source, size, smartAsset, asset, s3Source);
      } else if (this.type === 'video' && isAddAction) {
        this.#createVideo(source, size, smartAsset, asset);
      } else if (this.type === 'video' && isReplaceAction) {
        this.#replaceVideo(source, smartAsset, asset, asset);
      } else if (this.type === 'text' && isAddAction) {
        const isEditingText = INKIVE.EditorFacade.canvas.getMode() === 'textedit';
        if (isEditingText) {
          this.#insertText(asset.source, smartAsset);
        } else {
          this.#createText(asset.source, smartAsset, size);
        }
      } else if (this.type === 'text' && isReplaceAction) {
        this.#updateText(asset.source, smartAsset);
      }

      layoutAttributeRef.current = null;
      layoutRef.current = null;
    }, []);

    return (
      <MenuShell visible={true}>
        <WorkspaceProvider>
          {this.collection === collection.user && <AssetManager workspaceAssetType={this.type}/>}
          <WorkspaceApp
            type={this.type}
            collection={this.collection}
            workspace={CONFIG.company_id}
            cardButtons={cardButtons}
            credentials={credentials}
            companionUrl={INKIVE.C.uppy_companion_url}
            onAssetClick={onAssetClick}
            permissions={permissions}
            bucketName={bucketName}
            objectKey={'virginia/company'}
            options={{
              isQa: INKIVE.C.IS_QA || INKIVE.C.IS_DEV
            }}
          />
        </WorkspaceProvider>
      </MenuShell>
    );
  };
}

export default WorkspaceView;
